import React, { createContext, useCallback, useContext, useEffect, useReducer } from 'react';
import toast from 'react-hot-toast';
import { blockchainApi } from '../api/smartTokenizerApi';
import { mapBlockchainTransaction } from '../api/smartTokenizerMappers';

const BlockchainContext = createContext();

const initialState = {
  isConnected: false,
  network: 'Besu Private Network',
  blockHeight: 0,
  gasPrice: 'N/A',
  transactions: [],
  transactionHistory: [],
  syncStatus: {
    syncing: false,
    percentage: 100,
    lastUpdated: null
  },
  peers: 0,
  nodeInfo: null,
  tokens: {
    totalSupply: 0,
    circulatingSupply: 0,
    burnedTokens: 0
  },
  loading: false,
  error: null
};

const BLOCKCHAIN_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_STATUS: 'SET_STATUS',
  ADD_TRANSACTION: 'ADD_TRANSACTION',
  UPDATE_TOKEN_INFO: 'UPDATE_TOKEN_INFO'
};

const blockchainReducer = (state, action) => {
  switch (action.type) {
    case BLOCKCHAIN_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case BLOCKCHAIN_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case BLOCKCHAIN_ACTIONS.SET_STATUS:
      return {
        ...state,
        ...action.payload,
        loading: false,
        error: null
      };
    case BLOCKCHAIN_ACTIONS.ADD_TRANSACTION: {
      const nextTransactions = [action.payload, ...state.transactions].slice(0, 100);
      return {
        ...state,
        transactions: nextTransactions,
        transactionHistory: nextTransactions
      };
    }
    case BLOCKCHAIN_ACTIONS.UPDATE_TOKEN_INFO:
      return {
        ...state,
        tokens: { ...state.tokens, ...action.payload }
      };
    default:
      return state;
  }
};

export const BlockchainProvider = ({ children }) => {
  const [state, dispatch] = useReducer(blockchainReducer, initialState);

  const refreshStatus = useCallback(async (silent = false) => {
    if (!silent) {
      dispatch({ type: BLOCKCHAIN_ACTIONS.SET_LOADING, payload: true });
    }

    try {
      const { data } = await blockchainApi.getStatus();
      const rpcOk = data.rpcReachable !== false && String(data.clientVersion || '').toLowerCase() !== 'unavailable';
      dispatch({
        type: BLOCKCHAIN_ACTIONS.SET_STATUS,
        payload: {
          isConnected: rpcOk,
          network: data.networkName || 'Besu Private Network',
          blockHeight: data.currentBlock || 0,
          gasPrice: 'N/A',
          peers: state.peers || 0,
          nodeInfo: {
            clientVersion: data.clientVersion,
            chainId: data.chainId,
            observerHttpUrl: data.observerHttpUrl,
            observerWsUrl: data.observerWsUrl,
            offlineSigningRequired: data.offlineSigningRequired,
            rpcReachable: data.rpcReachable
          },
          syncStatus: {
            syncing: false,
            percentage: 100,
            lastUpdated: new Date().toISOString()
          }
        }
      });
    } catch (error) {
      dispatch({
        type: BLOCKCHAIN_ACTIONS.SET_STATUS,
        payload: {
          isConnected: false,
          syncStatus: {
            syncing: false,
            percentage: 0,
            lastUpdated: new Date().toISOString()
          }
        }
      });
      dispatch({
        type: BLOCKCHAIN_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || error.message
      });
    }
  }, [state.peers]);

  useEffect(() => {
    refreshStatus();
    const interval = setInterval(() => refreshStatus(true), 15000);
    return () => clearInterval(interval);
  }, [refreshStatus]);

  const addLocalTransaction = (transaction) => {
    dispatch({ type: BLOCKCHAIN_ACTIONS.ADD_TRANSACTION, payload: transaction });
    return transaction;
  };

  const sendTransaction = async (payload = {}) => {
    const transaction = {
      id: `tx_${Date.now()}`,
      type: payload.type || 'custom',
      amount: payload.amount || payload.quantity || 0,
      itemId: payload.itemId || payload.assetId || null,
      timestamp: new Date().toISOString(),
      txHash: `0x${Math.random().toString(16).slice(2).padEnd(64, '0').slice(0, 64)}`,
      status: 'submitted',
      from: payload.from || '',
      to: payload.to || payload.recipient || ''
    };

    addLocalTransaction(transaction);
    toast.success('Transaction queued locally for tracking');
    return transaction;
  };

  const mintTokens = async (amountOrPayload, itemId) => {
    const payload =
      typeof amountOrPayload === 'object'
        ? amountOrPayload
        : { amount: amountOrPayload, itemId, type: 'mint' };

    const transaction = await sendTransaction({ ...payload, type: payload.type || 'mint' });
    dispatch({
      type: BLOCKCHAIN_ACTIONS.UPDATE_TOKEN_INFO,
      payload: {
        totalSupply: state.tokens.totalSupply + Number(payload.amount || payload.quantity || 1),
        circulatingSupply:
          state.tokens.circulatingSupply + Number(payload.amount || payload.quantity || 1)
      }
    });
    return transaction;
  };

  const burnTokens = async (amount, itemId) => {
    const transaction = await sendTransaction({ amount, itemId, type: 'burn' });
    dispatch({
      type: BLOCKCHAIN_ACTIONS.UPDATE_TOKEN_INFO,
      payload: {
        circulatingSupply: Math.max(0, state.tokens.circulatingSupply - Number(amount || 0)),
        burnedTokens: state.tokens.burnedTokens + Number(amount || 0)
      }
    });
    return transaction;
  };

  const lookupTransaction = useCallback(async (transactionHash) => {
    try {
      const { data } = await blockchainApi.getTransaction(transactionHash);
      if (!data) {
        return null;
      }

      const transaction = mapBlockchainTransaction(data);
      dispatch({ type: BLOCKCHAIN_ACTIONS.ADD_TRANSACTION, payload: transaction });
      return transaction;
    } catch (error) {
      toast.error('Transaction lookup failed');
      throw error;
    }
  }, []);

  const getTransactionStatus = (txHash) => {
    const transaction = state.transactions.find(
      (item) => item.txHash === txHash || item.hash === txHash
    );

    return transaction ? transaction.status : 'not_found';
  };

  const value = {
    ...state,
    currentBlock: state.blockHeight,
    latestBlock: state.blockHeight,
    connectedPeers: state.peers,
    reconnect: refreshStatus,
    refreshStatus,
    sendTransaction,
    mintTokens,
    burnTokens,
    lookupTransaction,
    getTransactionStatus
  };

  return <BlockchainContext.Provider value={value}>{children}</BlockchainContext.Provider>;
};

export const useBlockchain = () => {
  const context = useContext(BlockchainContext);
  if (!context) {
    throw new Error('useBlockchain must be used within a BlockchainProvider');
  }
  return context;
};